import { useCallback, useEffect, useState } from 'react';
import { api } from './api.js';

/**
 * Web push opt-in (docs/05 "PWA behavior", docs/09).
 *
 * The permission prompt is never shown on a first visit. It is offered only
 * after a passenger has something to be notified *about* — a confirmed booking —
 * and framed with the value ("departure reminders and schedule changes"), because
 * a dismissed prompt is effectively permanent.
 */

const PROMPTED_KEY = 'busbook.push.prompted';

export type PushState = 'unsupported' | 'unavailable' | 'default' | 'granted' | 'denied';

/** VAPID keys arrive base64url-encoded; PushManager wants raw bytes. */
function base64ToBytes(base64: string): ArrayBuffer {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4))
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const raw = atob(padded);
  const buffer = new ArrayBuffer(raw.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i++) view[i] = raw.charCodeAt(i);
  return buffer;
}

function supported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export function usePush() {
  const [state, setState] = useState<PushState>('unsupported');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported()) {
      setState('unsupported');
      return;
    }
    void (async () => {
      try {
        const key = await api.request<{ enabled: boolean; publicKey: string }>('/public/push/key', {
          method: 'GET',
          auth: false,
        });
        // The platform may simply not have VAPID keys configured.
        setState(key.enabled ? (Notification.permission as PushState) : 'unavailable');
      } catch {
        setState('unavailable');
      }
    })();
  }, []);

  const subscribe = useCallback(async (): Promise<boolean> => {
    if (!supported()) return false;
    setBusy(true);
    try {
      const key = await api.request<{ enabled: boolean; publicKey: string }>('/public/push/key', {
        method: 'GET',
        auth: false,
      });
      if (!key.enabled || !key.publicKey) {
        setState('unavailable');
        return false;
      }

      const permission = await Notification.requestPermission();
      setState(permission as PushState);
      try {
        localStorage.setItem(PROMPTED_KEY, '1');
      } catch {
        /* storage may be unavailable; the prompt simply may reappear */
      }
      if (permission !== 'granted') return false;

      const registration = await navigator.serviceWorker.ready;
      const existing = await registration.pushManager.getSubscription();
      const subscription =
        existing ??
        (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: base64ToBytes(key.publicKey),
        }));

      const json = subscription.toJSON() as { endpoint?: string; keys?: Record<string, string> };
      await api.request('/public/push/subscribe', {
        method: 'POST',
        auth: false,
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ endpoint: json.endpoint, keys: json.keys }),
      });
      return true;
    } catch {
      return false;
    } finally {
      setBusy(false);
    }
  }, []);

  const alreadyPrompted = (() => {
    try {
      return localStorage.getItem(PROMPTED_KEY) === '1';
    } catch {
      return false;
    }
  })();

  /** Only worth offering when it can actually be granted and has not been asked. */
  const shouldOffer = state === 'default' && !alreadyPrompted;

  return { state, busy, subscribe, shouldOffer };
}
